/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / ORDERS-RECEIPT-ACTIONS — Beleg-Aktion-Modal + Bon-Archivierung
   =============================================================================
   S67 Refactor: aus orders.js extrahiert (Zeilen 1973-2410, ~340 LOC).

   Block enthält:
     - openReceiptActionModal() / closeReceiptActionModal()
        Modal nach jedem „Bezahlt"-Klick oder nach Korrektur-Beleg-Ausstellung.
        Drei Aktionen: Versenden / Drucken / (S57: Archive entfernt — automatisch).

     - receiptActionSend()         Schickt Bon-Mail an customer_email
     - receiptActionPrint()        Browser-Print-Dialog
     - receiptActionSave()         HTML-Download als Blob
     - resendArchivedBonEmail()    „Beleg aus Archiv versenden" (Phase 31)

     - _ensureBonArchived()        GoBD §147 AO garantierte Archivierung
                                   (S57 Hard-Way: aus orderMarkPaid + submitCorrection
                                    synchron aufgerufen, idempotent über UNIQUE)

     - loadBonArchive()            DB-Cache-Reader (S29 Hard-Cut auf bon_archive Tabelle)
     - saveBonArchive()            @deprecated S29 No-Op

     - buildStandaloneReceiptHtml() Standalone-HTML-Bon mit eingebettetem QR (S54)

   Cross-Module-Calls:
     - closeReceiptActionModal() lokal — bleibt sauber innerhalb des Moduls.
     - sendOrderReceiptEmail / printOrderReceipt / renderOrderReceipt aus
       Email-Templates und Receipt-Renderer-Modul.
     - Wird NICHT von orders-correction.js zurückgerufen — Receipt-Action ist
       semantisch stromabwärts, kein Circular.
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { printOrderReceipt, renderOrderReceipt } from './receipt.js';
// S254 — herausgelöst, damit die Gast-Seite ihn ohne dieses Modul bekommt.
// IMPORT (nicht nur Re-Export): `resendArchivedBonEmail` und
// `openArchivedBonPdf` rufen ihn hier selbst auf — ein blosses
// `export … from` brächte den Namen nicht in den lokalen Scope
// (Memory `gg-module-extract`: stiller ReferenceError zur Laufzeit).
import { buildStandaloneReceiptHtml } from './receipt-standalone.js';
import { sendOrderReceiptEmail, _buildReceiptPdfAttachment } from '../../core/email-templates.js';
// S225 CUT B — Kind-Toggle-Spiegel für den Beleg-Erneut-Versand (order_receipt_resend).
import { isMailKindEnabled } from '../../core/mail-kinds.js';

// -----------------------------------------------------------------------------
// Lokaler State-Helper
// -----------------------------------------------------------------------------
function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { orders: [], settings: {} };
}

// -----------------------------------------------------------------------------
// Modul-private Referenz, damit die inline-Buttons im Modal sauberen Zugriff haben.
// -----------------------------------------------------------------------------
let _receiptActionOrder = null;
let _receiptActionKind  = 'paid';
let _receiptActionOnClose = null;   // S135 P4 (2b) — Callback nach Schließen (Split-Schleife)

export function openReceiptActionModal(order, opts) {
  if (!order || !order.id) return;
  opts = opts || {};
  _receiptActionOrder = order;
  _receiptActionKind  = opts.kind === 'correction' ? 'correction' : 'paid';
  _receiptActionOnClose = (typeof opts.onClose === 'function') ? opts.onClose : null;

  const isCorr = _receiptActionKind === 'correction';
  const num    = htmlEscape(String(order.orderNumber || order.id));
  const inv    = order.invoiceNumber ? htmlEscape(String(order.invoiceNumber)) : '—';
  const email  = order.customerEmail || '';
  const gross  = ((order.totalGrossCents || 0) / 100)
                   .toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const labelHead = isCorr
    ? `Stornobeleg ${num} ausgestellt`
    : `Bestellung ${num} bezahlt`;
  const labelHint = isCorr
    ? `Rg. <strong>${inv}</strong> · Erstattung ${gross} €`
    : `Rg. <strong>${inv}</strong> · ${gross} €`;
  // S134 P1: Liegt keine E-Mail vor, wird die Kachel zum Inline-Eingabe-
  // feld („E-Mail nachtragen") statt nur ausgegraut — Operator kann die
  // Adresse direkt erfassen, wir schreiben sie auf die Order (+ Kunde) und
  // versenden den Bon. Liegt eine E-Mail vor: normale Versand-Kachel.
  const mailTileHtml = email
    ? `
          <button class="receipt-action-tile" onclick="receiptActionSend()">
            <div class="receipt-action-icon">✉</div>
            <div class="receipt-action-label">Bon versenden</div>
            <div class="receipt-action-desc">Per Mail an den Kunden</div>
            <div class="hint" style="margin-top:6px;">Empfänger: ${htmlEscape(email)}</div>
          </button>`
    : `
          <div class="receipt-action-tile receipt-action-tile-form">
            <div class="receipt-action-icon">✉</div>
            <div class="receipt-action-label">E-Mail nachtragen</div>
            <div class="receipt-action-desc">Keine Adresse hinterlegt — eintragen & Bon senden</div>
            <input type="email" id="receipt-action-email-input" class="receipt-action-email-input"
                   placeholder="kunde@beispiel.de" autocomplete="email" inputmode="email">
            <button class="btn btn-primary btn-sm" id="receipt-action-email-send"
                    onclick="receiptActionAddEmailAndSend()">Speichern &amp; senden</button>
          </div>`;

  // S134 P3 — „Tisch jetzt freigeben?" nach dem Kassieren, wenn die Order einen
  // Tisch-Bezug hat (Walk-In via occupancy_id ODER Reservierung via
  // reservation_id). Ja → Tisch sofort frei · Nein → läuft regulär aus.
  // Nur im Bezahl-Modal (nicht bei Stornobeleg).
  const hasTable = !isCorr && !!(order.occupancyId || order.reservationId);
  const releaseSectionHtml = hasTable ? `
        <div class="receipt-release-section" id="receipt-release-section">
          <div class="receipt-release-q">🍽️ <strong>Tisch jetzt freigeben?</strong></div>
          <div class="receipt-release-hint">Gast hat bezahlt — soll der Tisch sofort wieder buchbar sein, oder läuft die Belegung regulär aus?</div>
          <div class="receipt-release-actions">
            <button class="btn btn-primary btn-sm" onclick="receiptReleaseTable()">Ja, Tisch freigeben</button>
            <button class="btn btn-ghost btn-sm" onclick="receiptReleaseDismiss()">Nein, läuft regulär aus</button>
          </div>
        </div>` : '';

  // Modal-Element bauen
  const modal = document.createElement('div');
  modal.className = 'order-modal-overlay receipt-action-overlay';
  modal.id = 'receipt-action-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.innerHTML = `
    <div class="order-modal receipt-action-modal" onclick="event.stopPropagation()">
      <header class="order-modal-header">
        <div>
          <div class="order-modal-eyebrow${isCorr ? '' : ''}" style="${isCorr ? 'color:var(--danger);' : ''}">
            ${isCorr ? 'Stornobeleg' : 'Belegausgabepflicht'}
          </div>
          <h2 class="order-modal-title">${labelHead}</h2>
          <div class="hint" style="margin-top:4px;">${labelHint}</div>
        </div>
        <button class="order-modal-close" onclick="closeReceiptActionModal()" aria-label="Schließen">×</button>
      </header>

      <div class="order-modal-body">
        ${releaseSectionHtml}
        <p class="hint" style="margin-bottom:14px;">
          ${isCorr
            ? 'Wie soll der Stornobeleg an den Kunden übergeben werden?'
            : 'Gemäß § 146a AO ist die Belegausgabe Pflicht. Wie soll der Bon übergeben werden?'}
        </p>

        <div class="receipt-action-grid">
          ${mailTileHtml}

          <button class="receipt-action-tile" onclick="receiptActionPrint()">
            <div class="receipt-action-icon">🖨️</div>
            <div class="receipt-action-label">Bon drucken</div>
            <div class="receipt-action-desc">Browser-Druckdialog (mit «Als PDF» kombinierbar)</div>
          </button>
        </div>

        <div class="receipt-action-archive-note">
          🗄️ <strong>Bereits automatisch archiviert</strong> — sichtbar in Auswertung → Archiv → Bons.
        </div>

        <div class="receipt-action-foot">
          <button class="btn btn-ghost btn-sm" onclick="closeReceiptActionModal()">Später erledigen</button>
        </div>
      </div>
    </div>
  `;
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeReceiptActionModal();
  });
  const onKey = (e) => {
    if (e.key === 'Escape') { closeReceiptActionModal(); document.removeEventListener('keydown', onKey); }
  };
  document.addEventListener('keydown', onKey);
  document.body.appendChild(modal);
}

export function closeReceiptActionModal() {
  const m = document.getElementById('receipt-action-modal');
  if (m) m.remove();
  _receiptActionOrder = null;
  // S135 P4 (2b) — Split-Schleife: nach dem Schließen ggf. zurück in den
  // Warenkorb (nächster Teilbeleg) bzw. Abschluss. Einmalig feuern.
  const cb = _receiptActionOnClose;
  _receiptActionOnClose = null;
  if (cb) { try { cb(); } catch (e) { console.warn('[receipt-action onClose]', e); } }
}

export async function receiptActionSend() {
  if (!_receiptActionOrder) return;
  const o = _receiptActionOrder;
  if (!o.customerEmail) { showToast('Keine Mail-Adresse vorhanden.', 'error'); return; }
  try {
    // S213-C1 — server-seitiger Versand (enqueue). sendOrderReceiptEmail ist jetzt
    // der Enqueue-Wrapper; Erfolg = „in Warteschlange" (Cron sendet ~60 s später).
    const res = await sendOrderReceiptEmail(o);
    // S225-HF6 — Kind-Toggle-Gate (kind_disabled) hat bereits einen Info-Toast
    // gezeigt: kein Fehler-Toast, Modal offen lassen. `skipped` deckt auch den
    // (hier nicht erreichbaren) no-email-Skip ab.
    if (res && res.skipped) return;
    if (res && res.success === false) {
      showToast('Konnte nicht in die Warteschlange stellen: ' + (res.error || 'unbekannt'), 'error');
      return;
    }
    showToast(`Beleg an ${o.customerEmail} wird versendet (Warteschlange).`, 'success');
    closeReceiptActionModal();
  } catch (e) {
    console.error('[receiptActionSend]', e);
    showToast('Versand fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

/**
 * S134 P1 — E-Mail im Bon-Modal nachtragen und Bon versenden.
 *
 * Liegt zur Order keine customer_email vor, rendert das Modal statt der
 * ausgegrauten Mail-Kachel ein Inline-Eingabefeld. Dieser Handler:
 *   1. validiert die Eingabe,
 *   2. schreibt die E-Mail über `set_order_customer_email` auf die Order
 *      (Order ist idR `paid` → GoBD-Bypass nur fürs Kontaktfeld, in der RPC),
 *   3. pflegt den Kundendatensatz (Telefon-/Namens-Match → Update, sonst
 *      Anlage; nicht-fatal),
 *   4. versendet den Bon über `sendOrderReceiptEmail`.
 *
 * Der Re-Render ist non-destruktiv: das Modal bleibt stehen, nur Button/Input
 * gehen während des Sendens in den Lade-Zustand; bei Erfolg schließt es.
 */
export async function receiptActionAddEmailAndSend() {
  if (!_receiptActionOrder) return;
  const o = _receiptActionOrder;
  const input = document.getElementById('receipt-action-email-input');
  const btn   = document.getElementById('receipt-action-email-send');
  const email = ((input && input.value) || '').trim();
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
    showToast('Bitte eine gültige E-Mail-Adresse eingeben.', 'error');
    if (input) input.focus();
    return;
  }
  if (btn)   { btn.disabled = true; btn.textContent = 'Sende…'; }
  if (input) { input.disabled = true; }
  try {
    // 1) E-Mail auf die Order schreiben (paid → GoBD-Bypass-RPC).
    const updated = await window.App.storage.orders.setCustomerEmail(o.id, email);
    o.customerEmail = (updated && updated.customerEmail) || email;

    // 2) Kundendatensatz pflegen — nur wenn Kontaktanker (Telefon/Name) da
    //    ist. Telefon-Match aktualisiert den bestehenden Kunden, sonst Anlage
    //    (upsert_customer_from_contact). Fehler hier sind nicht-fatal.
    try {
      const phone = (o.customerPhone || '').trim();
      const name  = (o.customerName  || '').trim();
      if (window.App.storage.customers && (phone || name)) {
        await window.App.storage.customers.upsert({ email, phone: phone || null, name: name || null });
      }
    } catch (custErr) {
      console.warn('[receiptActionAddEmailAndSend] Kunden-Upsert nicht-fatal:', custErr);
    }

    // 3) Bon versenden (S213-C1 — server-seitig, enqueue → „Warteschlange").
    const res = await sendOrderReceiptEmail(o);
    // S225-HF6 — Kind-Toggle-Gate (kind_disabled) hat bereits getoastet: kein
    // Fehler-Toast, Modal offen + Eingabe/Button wieder freigeben.
    if (res && res.skipped) {
      if (btn)   { btn.disabled = false; btn.textContent = 'Speichern & senden'; }
      if (input) { input.disabled = false; }
      return;
    }
    if (res && res.success === false) {
      showToast('Konnte nicht in die Warteschlange stellen: ' + (res.error || 'unbekannt'), 'error');
      if (btn)   { btn.disabled = false; btn.textContent = 'Speichern & senden'; }
      if (input) { input.disabled = false; }
      return;
    }
    showToast(`Beleg an ${email} wird versendet (Warteschlange).`, 'success');
    closeReceiptActionModal();
  } catch (e) {
    console.error('[receiptActionAddEmailAndSend]', e);
    // OPERATOR_REQUIRED öffnet bereits global das PIN-Overlay (_throwWriteError).
    if (!(e && e.operatorRequired)) {
      showToast('Konnte nicht senden: ' + (e.message || e), 'error');
    }
    if (btn)   { btn.disabled = false; btn.textContent = 'Speichern & senden'; }
    if (input) { input.disabled = false; }
  }
}

/**
 * Phase 31 / Block C (Session 29): „Beleg aus Archiv versenden".
 *
 * Hängt den archivierten Bon-HTML-Snapshot (`entry.html` aus localStorage)
 * in eine Mail und schickt sie raus. Empfänger-Reihenfolge:
 *   1. customer_email aus dem Archiv-Eintrag (idR die echte Kunden-Adresse)
 *   2. settings.accountantEmail (Steuerberater-Fallback)
 *   3. settings.restaurantEmail (letzter Ausweg, idR die Restaurant-Mail)
 *
 * Logo wird vor Versand inline (Data-URL bei Default-Asset, Storage-URL
 * bleibt absolut) — `inlineBrandLogoForEmail()` im email-templates-Modul
 * macht das transparent.
 */
/**
 * S183 — reine Aufbereitung der Resend-Mail-Metadaten (Betreff, Begleittext,
 * PDF-Dateiname, outboxMeta) aus dem Archiv-Eintrag. Pure (Vitest).
 */
export function buildResendReceiptMeta(entry, orderId, brand) {
  const e = entry || {};
  const belegId = e.invoiceNumber || e.orderNumber || orderId;
  const isCorr = !!e.isCorrection;
  const label = isCorr ? 'Stornobeleg' : 'Kassenbon';
  const subject = `${label} ${belegId} · ${brand}`;
  const intro =
    `Hallo${e.customerName ? ' ' + e.customerName : ''},\n\n` +
    `anbei findest du den ${label} zu Bestellung ${belegId} als PDF (aus unserem Archiv).\n\n` +
    `Bei Rückfragen erreichst du uns gerne.\n\n— dein ${brand}-Team`;
  return {
    belegId, isCorr, subject, intro,
    filenameBase: `${label}-${belegId}`,
    outboxMeta: {
      kind: 'order_receipt_resend',
      order_id: orderId,
      invoice_number: e.invoiceNumber || null,
      is_correction: isCorr
    }
  };
}

/**
 * S240-HF7 — archivierten Bon-Snapshot lazy holen.
 *
 * Der Listen-Hydrate lädt `html` nicht mehr mit (164 Bons × Ø 116 kB = 23 MB pro
 * Boot, zwei parallele Requests liefen in den statement timeout und lieferten
 * dann ein LEERES Archiv ohne Fehler). Im Cache steht `html: null` = „noch nicht
 * geladen"; wer den Snapshot wirklich braucht, holt ihn hier.
 * Fällt auf den Cache zurück, falls der Adapter `getHtml` (noch) nicht kennt.
 */
async function _archivedBonHtml(orderId) {
  const ba = window.App && window.App.storage && window.App.storage.bonArchive;
  if (ba && typeof ba.getHtml === 'function') {
    try { return (await ba.getHtml(orderId)) || ''; }
    catch (e) { console.error('[orders-receipt-actions] getHtml:', e); return ''; }
  }
  const entry = (loadBonArchive() || {})[orderId];
  return (entry && entry.html) || '';
}

/**
 * S213-C1 — pdf_path-Garantie für einen archivierten Bon (Alt-Bon-Lazy).
 * Ist das PDF schon abgelegt (Regelfall seit phase_159), nichts tun. Sonst aus
 * dem archivierten `html` erzeugen → hochladen → `setPdfPath` (owner/admin, RLS;
 * best-effort). Liefert den Pfad oder null. Kein Blockieren.
 */
async function _ensureBonPdfPath(orderId) {
  const ba = window.App && window.App.storage && window.App.storage.bonArchive;
  if (!ba || !orderId) return null;
  const entry = (loadBonArchive() || {})[orderId];
  if (!entry) return null;
  if (entry.pdfPath) return entry.pdfPath;
  // S240-HF7: der Snapshot liegt nicht mehr im Listen-Hydrate → lazy holen.
  const html = await _archivedBonHtml(orderId);
  if (!html) return null;
  const att = await _buildReceiptPdfAttachment(
    html, `Beleg-${entry.invoiceNumber || entry.orderNumber || orderId}`);
  const base64 = att && att[0] && att[0].content;
  if (!base64) return null;
  const path = await ba.uploadPdfBase64(orderId, base64);
  try { await ba.setPdfPath(orderId, path); }
  catch (e) { console.warn('[resendArchivedBonEmail] pdf_path-Backfill übersprungen (RLS/owner-only):', e); }
  return path;
}

export async function resendArchivedBonEmail(orderId) {
  if (!orderId) return;
  let archive;
  try { archive = loadBonArchive(); }
  catch (_) { archive = {}; }
  const entry = archive && archive[orderId];
  // S240-HF7: `entry.html` ist im Listen-Cache `null` (lazy) — die Existenz des
  // Archiv-Eintrags ist das Kriterium, den Snapshot holt `_ensureBonPdfPath`.
  if (!entry) {
    showToast('Kein archivierter Bon-Snapshot zu dieser Bestellung gefunden.', 'error');
    return;
  }

  const state = getState();
  const s = state.settings || {};
  const to = (entry.customerEmail || s.accountantEmail || s.restaurantEmail || '').trim();
  if (!to) {
    showToast('Kein Empfänger gesetzt — bitte Kunden- oder Steuerberater-Mail prüfen.', 'error');
    return;
  }

  const supa = window.App && window.App.supabase;
  if (!supa || typeof supa.rpc !== 'function') {
    showToast('Supabase nicht verfügbar.', 'error');
    return;
  }

  // S225 CUT B — Kind-Toggle-Spiegel: „Beleg erneut senden" deaktiviert → kein
  // Enqueue, aktiver Feedback-Toast (Operator klickt bewusst).
  if (!isMailKindEnabled(getState().settings, 'order_receipt_resend')) {
    showToast('Der Beleg-Erneut-Versand ist deaktiviert (Einstellungen → Vorlagen → Belege).', 'info');
    return;
  }

  try {
    // S213-C1 — Ein-Pfad: pdf_path-GARANTIE zuerst (Alt-Bon lazy erzeugen +
    // hochladen + setPdfPath), damit die Edge das gespeicherte PDF sicher
    // anhängen kann; DANN server-seitig enqueuen (kind order_receipt_resend).
    await _ensureBonPdfPath(orderId);
    const { error } = await supa.rpc('enqueue_order_receipt_mail', {
      p_order_id: orderId, p_kind: 'order_receipt_resend', p_recipient_email: to
    });
    if (error) throw new Error(error.message || 'enqueue fehlgeschlagen');
    showToast(`Beleg an ${to} wird versendet (Warteschlange).`, 'success');
    if (window.App.events) {
      await window.App.events.emit('order:mail-sent', { orderId, kind: 'receipt_resend', to });
    }
  } catch (e) {
    console.error('[resendArchivedBonEmail]', e);
    showToast('Versand fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

/**
 * S134 P3 — „Ja, Tisch freigeben": gibt den Tisch der bezahlten Order sofort
 * frei. Walk-In (occupancy_id) → auto_end_occupancy_for_order beendet die
 * Belegung. Reservierung (reservation_id) → `table_released_at = jetzt`, womit
 * capacity.js die Tisch-Blockade ab sofort beendet (geplante Dauer bleibt).
 * Non-destruktiv: nur die Sektion wird zur Bestätigung umgerendert.
 */
export async function receiptReleaseTable() {
  const o = _receiptActionOrder;
  if (!o) return;
  const sect = document.getElementById('receipt-release-section');
  try {
    if (o.occupancyId) {
      if (!window.App || !window.App.supabase) throw new Error('Supabase nicht verfügbar.');
      const { error } = await window.App.supabase.rpc('auto_end_occupancy_for_order', { p_order_id: o.id });
      if (error) throw new Error(error.message || 'auto_end_occupancy_for_order fehlgeschlagen');
    } else if (o.reservationId) {
      if (!window.App || !window.App.storage || !window.App.storage.reservations) {
        throw new Error('Reservierungs-Adapter nicht verfügbar.');
      }
      await window.App.storage.reservations.update(o.reservationId, { tableReleasedAt: new Date().toISOString() });
    }
    if (sect) sect.innerHTML = '<div class="receipt-release-done">✓ Tisch freigegeben — wieder buchbar.</div>';
    showToast('Tisch freigegeben.', 'success');
  } catch (e) {
    console.error('[receiptReleaseTable]', e);
    if (!(e && e.operatorRequired)) {
      showToast('Konnte Tisch nicht freigeben: ' + (e.message || e), 'error');
    }
  }
}

/** S134 P3 — „Nein, läuft regulär aus": keine Aktion, Sektion bestätigt. */
export function receiptReleaseDismiss() {
  const sect = document.getElementById('receipt-release-section');
  if (sect) sect.innerHTML = '<div class="receipt-release-done">Tisch läuft regulär aus.</div>';
}

export function receiptActionPrint() {
  if (!_receiptActionOrder) return;
  try {
    printOrderReceipt(_receiptActionOrder);
    showToast('Druckdialog geöffnet.', 'success');
    closeReceiptActionModal();
  } catch (e) {
    console.error('[receiptActionPrint]', e);
    showToast('Druck fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

export async function receiptActionSave() {
  if (!_receiptActionOrder) return;
  try {
    const o = _receiptActionOrder;
    const html = await buildStandaloneReceiptHtml(o);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = (o.invoiceNumber || o.orderNumber || o.id || 'beleg').toString().replace(/[^a-z0-9-]+/gi, '-');
    a.href = url;
    a.download = `Beleg_${stamp}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    showToast('Beleg heruntergeladen.', 'success');
    closeReceiptActionModal();
  } catch (e) {
    console.error('[receiptActionSave]', e);
    showToast('Download fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

/**
 * Bon archivieren (F3, Session 20 Funktionstest; S29: DB-Migration).
 *
 * Persistiert eine kompakte Snapshot-Karte mit allen relevanten Bon-Daten +
 * Standalone-HTML in der `bon_archive`-Supabase-Tabelle (Migration 70).
 * Sichtbar im Tab "Auswertung → Archiv → Bons", der über
 * `App.storage.bonArchive.getCache()` aus dem In-Memory-State liest.
 *
 * GoBD-Aufbewahrungspflicht: 10 Jahre §147 AO.
 */
/**
 * S57 Hard-Way (Memory feedback_gg_db_for_critical_data + GoBD §147 AO):
 * Garantierte Bon-Archivierung — wird aus JEDEM Bezahl-Pfad synchron aufgerufen
 * (Auto-Pfad in orderMarkPaid, Storno-Pfad in submitCorrection). bonArchive.upsert
 * ist idempotent über UNIQUE(restaurant_id, order_id) — mehrfacher Aufruf ist sicher.
 *
 * Memory-Lesson: vorher war der einzige Pfad in bon_archive der manuelle Modal-
 * Button "🗄️ Bon archivieren". Wenn Personal "Später erledigen" klickte oder
 * das Modal schloss, war der Beleg nicht archiviert → GoBD-Verstoß. Mit S57
 * ist die Archivierung DB-First garantiert; das Modal hat nur noch Versand
 * und Druck als Aktionen (UX), keine Datenverantwortung mehr.
 *
 * Return: { archived: boolean, alreadyArchived: boolean, error?: string }.
 *
 * @param {object} order - Order-Objekt (camelCase oder snake_case akzeptiert).
 * @param {object} [opts] - { source: 'auto' | 'correction' }
 */
export async function _ensureBonArchived(order, opts = {}) {
  const source = opts.source || 'auto';
  // S238-HF4 — `insertOnly`: der Stripe-Catch-up darf sich NICHT auf den
  // In-Memory-Cache als Idempotenz-Quelle verlassen (bei leerem Cache nach
  // einem fehlgeschlagenen Hydrate hielt er belegte Orders für „ohne Bon" und
  // hat eine zweite Beleg-Mail ausgelöst). In diesem Modus schreibt der Aufruf
  // per INSERT — nur wer die Zeile wirklich NEU anlegt, ist Ersteller und darf
  // mailen; ein UNIQUE-Konflikt liefert `alreadyArchived: true`.
  const insertOnly = !!opts.insertOnly;
  if (!order || !order.id) return { archived: false, alreadyArchived: false, error: 'order.id fehlt' };

  try {
    if (!window.App || !window.App.storage || !window.App.storage.bonArchive) {
      throw new Error('Bon-Archiv-Storage nicht verfügbar.');
    }

    // Idempotenz-Check über In-Memory-Cache: wenn der Bon schon archiviert ist,
    // schreibe NICHT neu (würde sonst archived_at überschreiben und audit-Log
    // entstellen). Cache wird vom Realtime-Sub und upsert-Path synchron gehalten.
    const cache = (typeof window.App.storage.bonArchive.getCache === 'function')
      ? window.App.storage.bonArchive.getCache() : {};
    if (cache && cache[order.id]) {
      return { archived: true, alreadyArchived: true };
    }

    const html = await buildStandaloneReceiptHtml(order);

    // S213-A — Bon-PDF beim Archivieren EINMAL erzeugen und dauerhaft ablegen
    // (Jonas-MUSS: ein Renderer, ein gespeichertes Artefakt). Best-effort:
    // schlägt Erzeugung ODER Upload fehl, wird der Bon trotzdem (als html)
    // archiviert und pdf_path bleibt NULL (lazy-Nachzug im Archiv-UI) — der
    // Bezahl-Flow darf NIE blockieren oder rollen. _buildReceiptPdfAttachment
    // inlinet das Logo (html2canvas-tauglich) und liefert Base64.
    let pdfPath = null;
    try {
      const belegBase = `Beleg-${order.invoiceNumber || order.invoice_number
        || order.orderNumber || order.order_number || order.id}`;
      const att = await _buildReceiptPdfAttachment(html, belegBase);
      const base64 = att && att[0] && att[0].content;
      if (base64) {
        pdfPath = await window.App.storage.bonArchive.uploadPdfBase64(order.id, base64);
      } else {
        console.warn('[_ensureBonArchived] PDF-Erzeugung ohne Inhalt — html archiviert, pdf_path NULL.');
      }
    } catch (pdfErr) {
      console.warn('[_ensureBonArchived] Bon-PDF-Persistenz fehlgeschlagen (nicht-fatal):', pdfErr);
      if (typeof window.audit === 'function') {
        window.audit('bon.pdf_failed', {
          order_id: order.id,
          error: (pdfErr && pdfErr.message) || String(pdfErr)
        });
      }
    }

    const entry = {
      orderId:         order.id,
      orderNumber:     order.orderNumber || order.order_number || '',
      invoiceNumber:   order.invoiceNumber || order.invoice_number || '',
      isCorrection:    !!order.isCorrection || !!order.is_correction,
      correctsOrderId: order.correctsOrderId || order.corrects_order_id || null,
      customerName:    order.customerName || order.customer_name || '',
      customerEmail:   order.customerEmail || order.customer_email || '',
      paidAt:          order.paidAt || order.paid_at || new Date().toISOString(),
      paymentMethod:   order.paymentMethod || order.payment_method || '',
      totalGrossCents: Number(order.totalGrossCents || order.total_gross_cents || 0),
      tax7Cents:       Number(order.tax7Cents || order.tax_7_cents || 0),
      tax19Cents:      Number(order.tax19Cents || order.tax_19_cents || 0),
      pickupDate:      order.pickupDate || order.pickup_date || '',
      archivedAt:      new Date().toISOString(),
      html,
      pdfPath   // S213-A — im selben INSERT (RLS: INSERT für alle authentifiziert)
    };

    // S238-HF4: im Catch-up-Pfad entscheidet die DB, nicht der Cache.
    if (insertOnly && typeof window.App.storage.bonArchive.insertOnce === 'function') {
      const res = await window.App.storage.bonArchive.insertOnce(entry);
      if (!res || !res.inserted) {
        // Ein anderes Gerät (oder ein früherer Lauf) hat den Bon bereits
        // angelegt → kein Ersteller, also KEINE Beleg-Mail.
        return { archived: true, alreadyArchived: true };
      }
    } else {
      await window.App.storage.bonArchive.upsert(entry);
    }

    // Audit-Trail-Eintrag über den globalen audit()-Helper (src/core/audit.js).
    // Der Helper fängt eigene Fehler ab, daher kein zusätzlicher try/catch nötig.
    if (typeof window.audit === 'function') {
      window.audit(
        source === 'correction' ? 'bon.archived_correction' : 'bon.archived_auto',
        {
          order_id: order.id,
          invoice_number: entry.invoiceNumber,
          payment_method: entry.paymentMethod,
          is_correction: entry.isCorrection,
          source
        }
      );
    }

    return { archived: true, alreadyArchived: false };
  } catch (e) {
    console.error('[_ensureBonArchived]', e);
    return { archived: false, alreadyArchived: false, error: (e && e.message) || String(e) };
  }
}

// -----------------------------------------------------------------------------
// S213-A — „Bon als PDF" aus dem Archiv, in einem In-App-Modal (kein neuer Tab).
// -----------------------------------------------------------------------------

// Aktuell im Modal angezeigte blob:-URL — beim Schließen freigeben (kein Leak).
let _bonPdfObjectUrl = null;

/** Base64 (ohne data:-Prefix) → PDF-Blob (für die blob:-iframe-Anzeige). */
function _base64ToPdfBlob(base64) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: 'application/pdf' });
}

/**
 * „Bon als PDF" — zeigt den Beleg in einem In-App-Modal (iframe, blob:-URL;
 * Memory feedback_gg_inline_over_native_dialogs — kein neuer Browser-Tab, kein
 * Popup-Blocker). Ist der Bon bereits persistiert (pdf_path), wird das
 * gespeicherte Objekt geladen; fehlt es (Alt-Bon vor phase_159 oder frühere
 * Erzeugung schlug fehl), wird das PDF client-seitig aus dem archivierten
 * `html` erzeugt, best-effort in den Bucket hochgeladen (Backfill; das
 * pdf_path-UPDATE greift nur als owner/admin — RLS) und direkt angezeigt.
 */
export async function openArchivedBonPdf(orderId) {
  if (!orderId) return;
  const bonArchive = window.App && window.App.storage && window.App.storage.bonArchive;
  if (!bonArchive) { showToast('Bon-Archiv nicht verfügbar.', 'error'); return; }
  const entry = (loadBonArchive() || {})[orderId];
  if (!entry) {
    showToast('Kein archivierter Bon zu dieser Bestellung.', 'error');
    return;
  }

  const belegId = entry.invoiceNumber || entry.orderNumber || orderId;
  _openBonPdfModal(belegId);   // sofort: Modal mit Lade-Zustand

  try {
    let blob;
    if (entry.pdfPath) {
      // Gespeichertes PDF laden (RLS-geschützt über den auth-Client).
      blob = await bonArchive.downloadPdfBlob(entry.pdfPath);
    } else {
      // Alt-Bon: client-seitig aus html erzeugen (S240-HF7: lazy nachladen) …
      const html = await _archivedBonHtml(orderId);
      if (!html) throw new Error('Kein archivierter Bon-Snapshot vorhanden.');
      const att = await _buildReceiptPdfAttachment(html, `Beleg-${belegId}`);
      const base64 = att && att[0] && att[0].content;
      if (!base64) throw new Error('PDF konnte nicht erzeugt werden.');
      // … best-effort nachspeichern (Upload idempotent; setPdfPath owner/admin) …
      try {
        const path = await bonArchive.uploadPdfBase64(orderId, base64);
        try { await bonArchive.setPdfPath(orderId, path); }
        catch (bfErr) { console.warn('[openArchivedBonPdf] pdf_path-Backfill übersprungen (RLS/owner-only):', bfErr); }
      } catch (upErr) {
        console.warn('[openArchivedBonPdf] Upload übersprungen (nur Anzeige):', upErr);
      }
      // … und direkt aus dem Base64 anzeigen.
      blob = _base64ToPdfBlob(base64);
    }
    if (!blob || !blob.size) throw new Error('Kein PDF-Inhalt.');
    _fillBonPdfModal(blob, `Beleg-${belegId}.pdf`);
  } catch (e) {
    console.error('[openArchivedBonPdf]', e);
    _failBonPdfModal((e && e.message) || String(e));
  }
}

/**
 * S213-B-HF2 — Bon-PDF direkt aus einem Storage-Pfad im selben Modal öffnen
 * (Wiederverwendung durch Archiv → E-Mails: der delivery_receipt-Anhang trägt
 * pdf_path). Lädt das PDF als Blob (downloadPdfBlob, RLS-auth-Client) und zeigt
 * es im iframe — konsistent zu openArchivedBonPdf, kein neuer Tab.
 */
export async function openBonPdfByPath(pdfPath, titleLabel, filename) {
  const bonArchive = window.App && window.App.storage && window.App.storage.bonArchive;
  const downloader = (bonArchive && typeof bonArchive.downloadPdfBlob === 'function')
    ? (p) => bonArchive.downloadPdfBlob(p) : null;
  return openPdfByPath(pdfPath, titleLabel, filename, downloader);
}

/**
 * S264 — dasselbe Modal für PDFs aus einem anderen privaten Bucket
 * (Dienstplan → schedule-pdfs). `downloader(pdfPath) → Blob` kommt vom Aufrufer,
 * damit dieses Modul keinen zweiten Bucket kennen muss.
 */
export async function openPdfByPath(pdfPath, titleLabel, filename, downloader, opts) {
  const noun = (opts && opts.noun) || 'Bon-PDF';
  if (!pdfPath) { showToast('Kein PDF hinterlegt.', 'error'); return; }
  if (typeof downloader !== 'function') {
    showToast('PDF-Anzeige nicht verfügbar.', 'error');
    return;
  }
  _openBonPdfModal(titleLabel || 'Beleg');
  try {
    const blob = await downloader(pdfPath);
    if (!blob || !blob.size) throw new Error('Kein PDF-Inhalt.');
    _fillBonPdfModal(blob, filename || 'Beleg.pdf');
  } catch (e) {
    // S270 §B — ein geloeschtes PDF (Aufbewahrung) ist ein erwarteter Zustand:
    // Info statt Fehler, und der Text des Aufrufers ohne „Bon-PDF"-Vorspann.
    if (e && e.code === 'not_found') console.info('[openPdfByPath] PDF nicht (mehr) vorhanden:', pdfPath);
    else console.error('[openPdfByPath]', e);
    _failBonPdfModal((e && e.message) || String(e), noun);
  }
}

/** Modal-Gerüst mit Lade-Zustand aufbauen (idempotent — ein Modal zur Zeit). */
function _openBonPdfModal(belegId) {
  closeBonPdfModal();
  // S213-B-HF3 — kein doppeltes „Beleg", wenn das Label (z. B. der Dateiname
  // „Beleg-GG-…") bereits so beginnt. openArchivedBonPdf übergibt nur die
  // Rechnungs-/Bestellnummer → „Beleg " wird ergänzt.
  const label = String(belegId || 'Beleg').trim();
  // S264 — Dienstplan-/Z-Bon-Titel tragen ihr eigenes Nomen, kein „Beleg " davor.
  const title = /^(beleg|dienstplan|z-bon)/i.test(label) ? label : `Beleg ${label}`;
  const modal = document.createElement('div');
  modal.className = 'order-modal-overlay bon-pdf-overlay';
  modal.id = 'bon-pdf-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.innerHTML = `
    <div class="bon-pdf-modal" onclick="event.stopPropagation()">
      <header class="bon-pdf-head">
        <div class="bon-pdf-title">🧾 ${htmlEscape(title)}</div>
        <div class="bon-pdf-actions">
          <a class="btn btn-ghost btn-sm bon-pdf-download" id="bon-pdf-download"
             style="display:none;" download>⬇ Herunterladen</a>
          <button class="order-modal-close" onclick="closeBonPdfModal()" aria-label="Schließen">×</button>
        </div>
      </header>
      <div class="bon-pdf-body" id="bon-pdf-body">
        <div class="bon-pdf-loading">Bon-PDF wird geladen…</div>
      </div>
    </div>
  `;
  modal.addEventListener('click', (e) => { if (e.target === modal) closeBonPdfModal(); });
  const onKey = (e) => {
    if (e.key === 'Escape') { closeBonPdfModal(); document.removeEventListener('keydown', onKey); }
  };
  document.addEventListener('keydown', onKey);
  document.body.appendChild(modal);
}

/** Geladenes PDF-Blob im Modal-iframe anzeigen + Download-Link scharf schalten. */
function _fillBonPdfModal(blob, filename) {
  const body = document.getElementById('bon-pdf-body');
  if (!body) return;   // Modal bereits geschlossen
  if (_bonPdfObjectUrl) { try { URL.revokeObjectURL(_bonPdfObjectUrl); } catch (_) {} }
  _bonPdfObjectUrl = URL.createObjectURL(blob);
  body.innerHTML = `<iframe class="bon-pdf-frame" src="${_bonPdfObjectUrl}" title="Bon-PDF"></iframe>`;
  const dl = document.getElementById('bon-pdf-download');
  if (dl) {
    dl.href = _bonPdfObjectUrl;
    dl.setAttribute('download', filename || 'beleg.pdf');
    dl.style.display = '';
  }
}

/** Fehler-Zustand im Modal (statt still schließen — Operator sieht den Grund). */
function _failBonPdfModal(msg, noun) {
  const what = noun || 'Bon-PDF';
  const body = document.getElementById('bon-pdf-body');
  if (!body) { showToast(what + ' fehlgeschlagen: ' + msg, 'error'); return; }
  body.innerHTML = `<div class="bon-pdf-error">⚠ ${htmlEscape(what)} konnte nicht geladen werden.<br>
    <span style="opacity:.7;font-size:.85em;">${htmlEscape(String(msg))}</span></div>`;
}

export function closeBonPdfModal() {
  const m = document.getElementById('bon-pdf-modal');
  if (m) m.remove();
  if (_bonPdfObjectUrl) { try { URL.revokeObjectURL(_bonPdfObjectUrl); } catch (_) {} _bonPdfObjectUrl = null; }
}

/**
 * S29 KRITISCH-Fix (Memory-Lesson gg-db-for-critical-data):
 * loadBonArchive liest jetzt aus dem In-Memory-Cache des Storage-Adapters

/**
 * S29 KRITISCH-Fix (Memory-Lesson gg-db-for-critical-data):
 * loadBonArchive liest jetzt aus dem In-Memory-Cache des Storage-Adapters
 * (`_state.bonArchive`), der beim Boot via `App.storage.bonArchive.hydrate()`
 * aus der `bon_archive`-Supabase-Tabelle befüllt wird. Kein localStorage
 * mehr — DB ist Single-Source-of-Truth, Cache nur im RAM.
 */
export function loadBonArchive() {
  try {
    if (window.App && window.App.storage && window.App.storage.bonArchive
        && typeof window.App.storage.bonArchive.getCache === 'function') {
      const obj = window.App.storage.bonArchive.getCache();
      return (obj && typeof obj === 'object') ? obj : {};
    }
  } catch (e) {
    console.warn('[loadBonArchive] state read failed:', e);
  }
  return {};
}

/**
 * @deprecated S29 — Bon-Archiv ist DB-only. Diese Funktion ist ein No-Op
 *   und nur noch vorhanden, falls externe Call-Sites sie referenzieren.
 *   Echtes Persistieren läuft über `App.storage.bonArchive.upsert(entry)`.
 */
// eslint-disable-next-line no-unused-vars
function saveBonArchive(_obj) { /* DB-only — no localStorage write */ }

// S29 KRITISCH-Fix: Die alte 4-MB-Notbremse und der localStorage-Schreibpfad
// wurden komplett entfernt. Persistenz läuft jetzt ausschließlich über die
// `bon_archive`-Supabase-Tabelle (Migration 70). Keine Größenbeschränkung
// mehr — Supabase-Tabellen wachsen problemlos auf GB-Ebene.

// Phase 30 (S28): viewArchivedBon + closeViewArchivedBon entfernt — Klick auf
// einen Bon im Archiv öffnet jetzt direkt openOrderDetail (Standard-Order-
// Detail-Modal), konsistent mit dem Orders-Sub-Tab im Archiv. Memory-Lesson
// `feedback_gg_archive_single_source`: kein Iframe-Modal mehr für Drill-Down.

// S254 — `buildStandaloneReceiptHtml` liegt jetzt in `receipt-standalone.js`
// (siehe Import oben). Re-Export, damit kein bestehender Aufrufer etwas merkt.
export { buildStandaloneReceiptHtml };

// -----------------------------------------------------------------------------
// Backward-Compat (inline-onclick-Handler in den Modal-Templates)
// -----------------------------------------------------------------------------

if (typeof window !== 'undefined') {
  Object.assign(window, {
    openReceiptActionModal,
    closeReceiptActionModal,
    receiptActionSend,
    receiptActionAddEmailAndSend,
    receiptReleaseTable,
    receiptReleaseDismiss,
    receiptActionPrint,
    receiptActionSave,
    // S57: receiptActionArchive entfernt — Auto-Archive übernimmt _ensureBonArchived.
    resendArchivedBonEmail,
    loadBonArchive,
    // S213-A — „Bon als PDF" im Archiv-Tab (onclick, CSP über dom-bindings).
    // In-App-Modal (iframe/blob:), kein neuer Tab; Schließen ebenfalls gebridged.
    openArchivedBonPdf,
    // S213-B-HF2 — dasselbe Modal per Storage-Pfad (Archiv → E-Mails → 📎-Chip).
    openBonPdfByPath,
    openPdfByPath,
    closeBonPdfModal,
    // S212 — das Fahrer-Terminal fährt beim Bar-Kassieren dieselbe GoBD-Bon-
    // Archivierung still im Hintergrund (kein Modal). Deshalb hier gebridged.
    _ensureBonArchived
  });
}
